import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { User, Moon, Sun, LogOut, BookOpen, Sparkles, Heart, Shield, Crown, Loader2, Check, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ProfilePage() {
  const { user, logout, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, lang, setLang, LANG_LIST, LANG_NAMES } = useLang();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [isPremium, setIsPremium] = useState(false);
  const [loadingPremium, setLoadingPremium] = useState(false);
  const [checkingPremium, setCheckingPremium] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState("yearly");

  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textSecondary = isDark ? "text-slate-300" : "text-slate-600";
  const textMuted = isDark ? "text-slate-400" : "text-slate-500";
  const labelColor = isDark ? "text-amber-400" : "text-amber-600";

  useEffect(() => { api().get("/subscription/check").then((r) => setIsPremium(r.data.is_premium)).catch(() => {}).finally(() => setCheckingPremium(false)); }, [api]);

  const handleSubscribe = async () => {
    setLoadingPremium(true);
    try { const res = await api().post("/subscription/create-checkout", { origin_url: window.location.origin, plan: selectedPlan }); if (res.data.url) window.location.href = res.data.url; }
    catch (e) { console.error(e); setLoadingPremium(false); }
  };

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        <h1 className={`font-heading text-2xl font-bold ${textPrimary} mb-8 ${isDark?"drop-shadow-lg":""}`}>{t("myProfile")}</h1>

        <div data-testid="profile-user-card" className="glass-card p-6 text-center mb-5 animate-fadeInUp relative">
          {isPremium && <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/20"><Crown size={12} className="text-amber-400" /><span className="text-[10px] text-amber-400 font-bold uppercase">Premium</span></div>}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500/30 to-purple-500/30 flex items-center justify-center mx-auto mb-4"><User size={36} className="text-amber-500" /></div>
          <h2 className={`font-heading text-xl font-bold ${textPrimary}`}>{user?.name}</h2>
          <p className={`${textMuted} text-sm mt-1`}>{user?.email}</p>
        </div>

        <div className="space-y-3">
          {/* Language */}
          <div className="glass-card p-4">
            <div className="flex items-center gap-3 mb-3"><Globe size={18} className="text-blue-500" /><span className={`text-sm font-medium ${textPrimary}`}>{t("language")}</span></div>
            <div className="grid grid-cols-4 gap-2">
              {LANG_LIST.map((l) => (
                <button key={l} data-testid={`profile-lang-${l}`} onClick={() => setLang(l)}
                  className={`py-2 rounded-xl text-xs font-semibold transition-all ${lang===l ? "bg-amber-500/80 text-white" : isDark ? "bg-white/5 text-slate-400 hover:bg-white/10" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                  {LANG_NAMES[l]}
                </button>
              ))}
            </div>
          </div>

          {/* Theme */}
          <button data-testid="theme-toggle-button" onClick={toggleTheme} className="w-full glass-card p-4 flex items-center justify-between hover:scale-[1.01] transition-all">
            <div className="flex items-center gap-3">{isDark?<Moon size={20} className="text-amber-400" />:<Sun size={20} className="text-amber-500" />}<span className={`text-sm font-medium ${textPrimary}`}>{isDark?t("darkMode"):t("lightMode")}</span></div>
            <div className={`w-12 h-6 rounded-full transition-all ${isDark?"bg-amber-500/80":"bg-slate-300"} relative`}><div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all ${isDark?"left-6":"left-0.5"}`} /></div>
          </button>

          {/* Premium */}
          <div className="glass-card p-5">
            <div className="flex items-center gap-3 mb-4"><Shield size={18} className="text-amber-500" /><p className={`text-sm font-bold ${textPrimary}`}>{t("premiumTitle")}</p></div>
            {checkingPremium ? <div className="flex items-center gap-2 py-2"><Loader2 size={14} className="animate-spin text-amber-400" /><p className={`text-xs ${textMuted}`}>{t("checking")}</p></div>
            : isPremium ? <div className="flex items-center gap-2 py-3"><Crown size={18} className="text-amber-400" /><p className={`text-sm font-semibold ${isDark?"text-emerald-400":"text-emerald-600"}`}>{t("premiumMember")}</p></div>
            : (<>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button data-testid="plan-monthly" onClick={() => setSelectedPlan("monthly")} className={`p-3 rounded-xl border-2 transition-all text-center ${selectedPlan==="monthly"?"border-amber-500 bg-amber-500/10":isDark?"border-white/10":"border-slate-200"}`}>
                  <p className={`text-[10px] uppercase tracking-wider ${textMuted} mb-1`}>{t("monthly")}</p><p className={`font-heading text-2xl font-bold ${textPrimary}`}>0,99€</p><p className={`text-[10px] ${textMuted}`}>{t("perMonth")}</p>
                </button>
                <button data-testid="plan-yearly" onClick={() => setSelectedPlan("yearly")} className={`p-3 rounded-xl border-2 transition-all text-center relative ${selectedPlan==="yearly"?"border-amber-500 bg-amber-500/10":isDark?"border-white/10":"border-slate-200"}`}>
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold uppercase">-33%</div>
                  <p className={`text-[10px] uppercase tracking-wider ${textMuted} mb-1`}>{t("yearly")}</p><p className={`font-heading text-2xl font-bold ${textPrimary}`}>7,99€</p><p className={`text-[10px] ${textMuted}`}>{t("perYear")}</p>
                </button>
              </div>
              <ul className={`text-xs ${textSecondary} space-y-2 mb-4`}>
                <li className="flex items-center gap-2"><Check size={12} className="text-amber-400" /> {t("unlimitedInterpretations")}</li>
                <li className="flex items-center gap-2"><Check size={12} className="text-amber-400" /> {t("noAds")}</li>
                <li className="flex items-center gap-2"><Check size={12} className="text-amber-400" /> {t("fullAccess")}</li>
              </ul>
              <button data-testid="subscribe-button" onClick={handleSubscribe} disabled={loadingPremium} className="w-full btn-gold py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {loadingPremium ? <><Loader2 size={16} className="animate-spin" /> {t("redirecting")}</> : <><Crown size={16} /> {t("subscribe")} — {selectedPlan==="yearly"?"7,99€"+t("perYear"):"0,99€"+t("perMonth")}</>}
              </button>
            </>)}
          </div>

          {/* About */}
          <div className="glass-card p-5">
            <p className={`text-[10px] ${labelColor} uppercase tracking-[0.15em] font-semibold mb-3`}>{t("about")}</p>
            <p className={`text-sm ${textSecondary} leading-relaxed`}>{t("aboutText")}</p>
            <div className={`flex items-center gap-4 mt-4 pt-3 border-t ${isDark?"border-white/8":"border-black/5"}`}>
              <div className={`flex items-center gap-1.5 text-[10px] ${textMuted}`}><BookOpen size={10} /> {t("journal")}</div>
              <div className={`flex items-center gap-1.5 text-[10px] ${textMuted}`}><Sparkles size={10} /> IA</div>
              <div className={`flex items-center gap-1.5 text-[10px] ${textMuted}`}><Heart size={10} /> {t("wellness")}</div>
            </div>
          </div>

          <button data-testid="logout-button" onClick={() => { logout(); navigate("/"); }} className="w-full glass-card p-4 flex items-center gap-3 hover:bg-red-500/10 transition-all">
            <LogOut size={18} className="text-red-500" /><span className="text-sm text-red-500 font-medium">{t("logout")}</span>
          </button>
        </div>

        {/* Legal links */}
        <div className={`flex items-center justify-center gap-3 mt-8 flex-wrap`}>
          <button onClick={() => navigate("/privacy")} className={`text-[10px] ${textMuted} hover:${labelColor} transition-colors underline underline-offset-2`}>
            {lang === "ar" ? "سياسة الخصوصية" : lang === "es" ? "Privacidad" : lang === "en" ? "Privacy" : "Confidentialité"}
          </button>
          <span className={`text-[10px] ${isDark ? "text-slate-700" : "text-slate-300"}`}>|</span>
          <button onClick={() => navigate("/terms")} className={`text-[10px] ${textMuted} hover:${labelColor} transition-colors underline underline-offset-2`}>
            {lang === "ar" ? "شروط الاستخدام" : lang === "es" ? "Términos" : lang === "en" ? "Terms" : "CGU"}
          </button>
          <span className={`text-[10px] ${isDark ? "text-slate-700" : "text-slate-300"}`}>|</span>
          <button onClick={() => navigate("/legal")} className={`text-[10px] ${textMuted} hover:${labelColor} transition-colors underline underline-offset-2`}>
            {lang === "ar" ? "إشعار قانوني" : lang === "es" ? "Aviso legal" : lang === "en" ? "Legal" : "Mentions légales"}
          </button>
        </div>
        <p className={`text-center ${isDark?"text-slate-600":"text-slate-400"} text-xs mt-3 mb-2`}>DreamNoor v1.0 — dreamnoor.com</p>
      </div>
      <BottomNav />
    </AppLayout>
  );
}
